import React, { useState, useEffect } from 'react';
import { Button, Typography } from 'antd';
import { WarningOutlined, InfoCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface DuplicateWarningDetail {
  label: string;
  value: string;
}

export interface DuplicateWarningItem {
  id: number;
  title: string;
  subtitle?: string;
  details?: DuplicateWarningDetail[];
  linkPath: string;
}

export interface DuplicateWarningProps {
  type?: 'warning' | 'info' | 'error';
  message: string;
  items?: DuplicateWarningItem[];
  defaultExpanded?: boolean;
  style?: React.CSSProperties;
}

export const DuplicateWarning: React.FC<DuplicateWarningProps> = ({
  type = 'warning',
  message,
  items = [],
  defaultExpanded,
  style,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const shouldBeExpanded =
    defaultExpanded !== undefined ? defaultExpanded : items.length === 1;

  const [expanded, setExpanded] = useState<boolean>(shouldBeExpanded);

  useEffect(() => {
    if (defaultExpanded !== undefined) {
      setExpanded(defaultExpanded);
    } else {
      setExpanded(items.length === 1);
    }
  }, [items.length, defaultExpanded]);

  if (!message && items.length === 0) return null;

  const isError = type === 'error';
  const isWarning = type === 'warning';

  const bgColor = isError ? '#fff2f0' : isWarning ? '#fffbe6' : '#e6f7ff';
  const borderColor = isError ? '#ffccc7' : isWarning ? '#ffe58f' : '#91d5ff';
  const iconColor = isError ? '#ff4d4f' : isWarning ? '#faad14' : '#1890ff';
  const titleColor = isError ? '#cf1322' : isWarning ? '#d48806' : '#096dd9';

  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 14,
        padding: '10px 14px',
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        fontSize: 13,
        lineHeight: '1.4',
        transition: 'all 0.2s ease',
        ...style,
      }}
    >
      {/* Header with Title, Count and Toggle Arrow */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: items.length > 0 ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={() => items.length > 0 && setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isError || isWarning ? (
            <WarningOutlined style={{ color: iconColor, fontSize: 15 }} />
          ) : (
            <InfoCircleOutlined style={{ color: iconColor, fontSize: 15 }} />
          )}
          <Text strong style={{ color: titleColor, fontSize: 13 }}>
            {message} {items.length > 0 && `( ${items.length} )`}
          </Text>
        </div>

        {items.length > 0 && (
          <Button
            type="text"
            size="small"
            style={{ color: titleColor, padding: '0 4px', display: 'flex', alignItems: 'center' }}
            icon={expanded ? <UpOutlined style={{ fontSize: 12 }} /> : <DownOutlined style={{ fontSize: 12 }} />}
          />
        )}
      </div>

      {/* Expanded Items List in Category/Event Card Style */}
      {expanded && items.length > 0 && (
        <div
          style={{
            marginTop: 10,
            background: '#ffffff',
            borderRadius: 6,
            border: '1px solid #f0f0f0',
            padding: '10px 14px',
          }}
        >
          {items.map((item, idx) => (
            <div
              key={item.id}
              style={{
                marginBottom: idx < items.length - 1 ? 10 : 0,
                borderBottom: idx < items.length - 1 ? '1px solid #f0f0f0' : 'none',
                paddingBottom: idx < items.length - 1 ? 8 : 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <strong style={{ fontSize: 13, color: '#262626' }}>{item.title}</strong>
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(item.linkPath, '_blank', 'noopener,noreferrer');
                  }}
                >
                  {t('View')}
                </Button>
              </div>

              {item.details && item.details.length > 0 ? (
                item.details.map((det, dIdx) => (
                  <p key={dIdx} style={{ margin: '2px 0 0', fontSize: 12, color: '#595959' }}>
                    <strong>{t(det.label)}:</strong> {det.value}
                  </p>
                ))
              ) : item.subtitle ? (
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#595959' }}>
                  {item.subtitle}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DuplicateWarning;
